import React, { useCallback, useEffect, useRef, useState } from 'react'
import { UnauthorisedTagsAlert } from '../components/UnauthorisedTagsAlert'
import { WaitingReceiptsAlert } from '../components/WaitingReceiptsAlert'
import { QueriesAlert } from '../components/QueriesAlert'
import { Link, useNavigate } from 'react-router-dom'
import pa from './ProductActivityPage.module.css'
import styles from './DashboardPage.module.css'
import o from '../components/Overlay.module.css'
import dov from './dashboard/Overlays.module.css'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { apiJson } from '../lib/api'
import { useHelpPref, usePreference } from '../lib/preferences'
import { fileTask, useTasks } from '../components/TaskCentre'
import { plural, rangeLabel } from './wages/format'
import { workbook, XLSX_TYPE } from './batches/xlsx'
import { addDaysLocal, todayLocal } from './timesheets/time'
import { DatePeriod, type Level } from './productActivity/DatePeriod'
import { initials } from './reports/model'
import { WeatherCard } from './dashboard/Weather'
import { ClockOffDialog, type ClockOffDone } from './dashboard/ClockOffDialog'
import { useLayer } from '../components/layers'
import { AsAt } from '../components/AsAt'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { useRefetch } from '../lib/connection'
import { useLive } from '../lib/live'
import {
  DASH_LEGEND,
  WIDGETS,
  WIDGETS_OPT_IN,
  WIDGET_GROUPS,
  dashPresets,
  dayLevel,
  dmy,
  kpiDelta,
  kpiValue,
  leaderTotal,
  mondayOfDate,
  money,
  normaliseDash,
  normaliseRoster,
  num,
  pickersOnly,
  rowCols,
  scopeOf,
  seasonStartOf,
  weekTitle,
  type DashResponse,
  type Kpi,
  type LeaderRow,
  type RosterResponse,
  type WidgetId,
  type WxLocation,
  filterRoster,
  NO_FILTER,
  NONE,
  rosterOptions,
  type RosterFilter,
} from './dashboard/model'

// Dashboard (handoff §7; V4 Dashboard). An overview of the period across the
// subscription: every card below the date period reads that period, except
// Clocked in now, which is this moment and says so. Nothing is edited here
// but Clock all off; every figure opens the screen that owns it. Which cards
// show, the weather location and the help panel are saved against the
// person (lib/preferences.ts).

// Clocked in now is pushed (h 4167); this poll runs only while the live
// stream is down, as the fallback.
const ROSTER_REFRESH_MS = 60_000
const MEDAL = ['#c9a227', '#a3a099', '#b07242']
const AVATAR = ['#c67139', '#7a8a5e']

async function dashLevels(user: { getIdToken: () => Promise<string> }, from: string, to: string) {
  const r = await apiJson<{ days: { work_date: string; cycles: number; shifts: number }[] }>(user, `/api/dashboard/days?from=${from}&to=${to}`)
  const m: Record<string, Level> = {}
  for (const d of r.days || []) m[d.work_date] = dayLevel(d.cycles, d.shifts)
  return m
}

export function DashboardPage() {
  const { user } = useAuth()
  const { currentWorkspace } = useWorkspace()
  const navigate = useNavigate()
  const today = todayLocal()
  const [range, setRange] = useState({ from: today, to: today })
  const [team, setTeam] = useState('')
  const [data, setData] = useState<DashResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [roster, setRoster] = useState<RosterResponse | null>(null)
  const [rosterError, setRosterError] = useState(false)
  const [help, setHelp] = useHelpPref(user, 'dashboard')
  const [off, setOff] = usePreference<WidgetId[]>(user, 'dashboard.widgets_off', [])
  const [location, setLocation] = usePreference<WxLocation | null>(user, 'weather.location', null)
  const [customOpen, setCustomOpen] = useState(false)
  const [clockOpen, setClockOpen] = useState(false)
  const [toast, setToast] = useState<{ text: string; undo?: string } | null>(null)
  const userRef = useRef(user)
  userRef.current = user
  const seq = useRef(0)
  const uid = user?.uid

  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const my = ++seq.current
    setLoading(true)
    try {
      const q = new URLSearchParams({ from: range.from, to: range.to })
      if (team) q.set('team', team)
      const r = normaliseDash(await apiJson<DashResponse>(u, `/api/dashboard?${q}`))
      if (my !== seq.current) return
      setData(r)
      setError(null)
      setAsAt(new Date())
    } catch (e: any) {
      if (my !== seq.current) return
      // The last figures stay up; the bar says they are not current.
      setError(e?.message || 'Could not load the dashboard')
    } finally {
      if (my === seq.current) setLoading(false)
    }
  }, [range.from, range.to, team, uid])

  const loadRoster = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    try {
      setRoster(normaliseRoster(await apiJson<RosterResponse>(u, `/api/dashboard/roster?today=${todayLocal()}`)))
      setRosterError(false)
    } catch {
      setRosterError(true)
    }
  }, [uid])

  useEffect(() => {
    load()
  }, [load])

  // A period that includes today is pushed: a cycle, batch or shift written on
  // one of its dates, or its wage basis rebuilt (the earnings and the
  // leaderboard), fetches the figures again. One that ends before today is
  // historical, stamped "as at" and refetched on focus or reconnect.
  const includesToday = range.from <= today && range.to >= today
  const live = useLive(user, ['cycles', 'batches', 'shifts', 'wages'], includesToday ? (d) => d >= range.from && d <= range.to : null, load)
  useRefetch(load)

  // Clocked in now: any shift change can close a session left open from an
  // earlier day, so every date counts (a scan's too: the roster shows the
  // last one, and fetching it again is cheap).
  const rosterLive = useLive(user, ['shifts', 'cycles'], () => true, loadRoster)
  useRefetch(loadRoster)
  useEffect(() => {
    loadRoster()
  }, [loadRoster])
  useEffect(() => {
    if (rosterLive === 'live') return
    const t = setInterval(loadRoster, ROSTER_REFRESH_MS)
    return () => clearInterval(t)
  }, [loadRoster, rosterLive])

  useEffect(() => {
    if (!toast || toast.undo) return
    const t = setTimeout(() => setToast(null), 3400)
    return () => clearTimeout(t)
  }, [toast])

  // Opt-in boards (Receipters) are the workspace's, the same for everyone and
  // turned on by an administrator; the rest are each person's own layout.
  const optIn = (id: WidgetId) => WIDGETS_OPT_IN.includes(id)
  const wsOn = data?.workspace.widgets_on || []
  const canSetWorkspace = !!data?.workspace.can_edit
  const shown = (id: WidgetId) => (optIn(id) ? wsOn.includes(id) : !off.includes(id))
  const setWorkspace = async (next: WidgetId[]) => {
    const before = wsOn
    const put = (w: WidgetId[]) => setData((d) => (d ? { ...d, workspace: { ...d.workspace, widgets_on: w } } : d))
    put(next)
    try {
      const r = await apiJson<{ widgets_on: WidgetId[] }>(userRef.current, '/api/dashboard/widgets', { method: 'PUT', body: { on: next } })
      put(r.widgets_on)
    } catch (e: any) {
      put(before)
      setToast({ text: e?.message || 'Could not change the board for the workspace' })
    }
  }
  const toggleWidget = (id: WidgetId) => {
    if (!optIn(id)) return setOff(shown(id) ? [...off, id] : off.filter((x) => x !== id))
    if (canSetWorkspace) setWorkspace(shown(id) ? wsOn.filter((x) => x !== id) : [...wsOn, id])
  }

  const clockedOff = (r: ClockOffDone) => {
    setClockOpen(false)
    setToast({ text: r.note ? `${r.flash}. ${r.note}` : r.flash, undo: r.batch_id })
    loadRoster()
    load()
  }
  const undo = async (batchId: string) => {
    setToast(null)
    try {
      const r = await apiJson<{ flash: string; note?: string }>(userRef.current, `/api/timesheets/clock-off/${batchId}/undo`, { body: {} })
      setToast({ text: r.note ? `${r.flash}. ${r.note}` : r.flash })
    } catch (e: any) {
      setToast({ text: e?.message || 'Could not undo the clock-off' })
    }
    loadRoster()
    load()
  }

  const scope = scopeOf(range.from, range.to, today)
  const setScope = (s: 'today' | 'week' | 'season') =>
    setRange({ from: s === 'today' ? today : s === 'week' ? mondayOfDate(today) : seasonStartOf(today), to: today })
  const name = currentWorkspace?.name
  const title = range.from === today && range.to === today ? (name ? `Today at ${name}` : 'Today') : name || 'Dashboard'

  const boards = shown('leaderboard') || shown('receipters')
  const top = rowCols(boards, shown('roster'))
  const bottom = rowCols(shown('weekly'), shown('weather'))

  // The receipters board sits above the leaderboard, which is then pickers only.
  const receipters = data?.receipters.rows || []
  const boardCards = (
    <>
      {shown('receipters') && <Leaderboard kind="receipters" data={data} rows={receipters} team={team} onTeam={setTeam} range={range} />}
      {shown('leaderboard') && (
        <Leaderboard
          kind="workers"
          data={data}
          rows={shown('receipters') ? pickersOnly(data?.leaderboard.rows || [], receipters) : data?.leaderboard.rows || []}
          team={team}
          onTeam={setTeam}
          onOpen={(r) => navigate(`/product-activity?${new URLSearchParams({ from: range.from, to: range.to, worker: r.name })}`)}
          range={range}
        />
      )}
    </>
  )

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <>
            <button
              type="button"
              className={headerStyles.btnPrimary}
              onClick={() => setCustomOpen(true)}
              aria-label="Customize dashboard"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
              </svg>
              Customize
            </button>
            <button
              type="button"
              className={headerStyles.btnHelp}
              onClick={() => setHelp(!help)}
              aria-expanded={help}
            >
              {help ? 'Hide help' : 'What this page is for'}
            </button>
          </>
        }
      />

      <PageBody>
        {help && (
          <div className={pa.helpGrid} data-testid="dashboard-help" style={{ position: 'relative' }}>
            <div className={pa.helpCard}>
              <span>What this page is for</span>
              <p>
                An overview of the day across your whole subscription: people on site, what the orchard produced, what is waiting on someone. Nothing is edited here. Every card is a way in
                to the screen that owns the detail.
              </p>
            </div>
            <div className={pa.helpCard}>
              <span>It is yours to arrange</span>
              <p>Customize turns cards on and off, so the page shows only what you watch. The layout is saved against you, so it follows you to any machine you sign in on.</p>
            </div>
            <div className={pa.helpCard}>
              <span>Missing a figure?</span>
              <p>
                Tell us which metric you watch and we will look at building it as a widget. If it is a number you check every morning, it belongs on this page rather than in a report you
                have to go and find.
              </p>
            </div>
            <button type="button" className={pa.closeX} style={{ position: 'absolute', top: '0.625rem', right: '0.625rem' }} title="Hide help" aria-label="Hide help" onClick={() => setHelp(false)}>
              ×
            </button>
          </div>
        )}

        <div className={pa.toolbar}>
          <DatePeriod
            user={user}
            from={range.from}
            to={range.to}
            onApply={(from, to) => setRange({ from, to })}
            presets={dashPresets}
            loadLevels={dashLevels}
            legend={DASH_LEGEND}
            edgeDot="#ffe1d0"
          />
          <div className={pa.viewTrack} role="group" aria-label="Period">
            {(
              [
                ['today', 'Today'],
                ['week', 'Week'],
                ['season', 'Season'],
              ] as const
            ).map(([k, label]) => (
              <button key={k} type="button" className={scope === k ? pa.viewOn : pa.view} aria-pressed={scope === k} onClick={() => setScope(k)}>
                {label}
              </button>
            ))}
          </div>
          {loading && data ? <span className={styles.scopeNote}>Updating…</span> : <AsAt at={asAt} live={includesToday && live === 'live'} className={styles.scopeNote} />}
        </div>

        {error && (
          <div className={pa.errorBar} role="alert">
            {data ? `Showing the last figures: ${error}` : error}
            <button type="button" className={pa.flashX} onClick={load}>
              Retry
            </button>
          </div>
        )}

        <UnauthorisedTagsAlert use={data?.unauthorised_tag_use} />
        <WaitingReceiptsAlert waiting={data?.waiting_tag_receipts} />
        <QueriesAlert />

        {shown('kpis') && (
          <>
            <div className={styles.kpis} data-testid="widget-kpis">
              {(data?.kpis || []).map((k) => (
                <KpiCard key={k.key} k={k} />
              ))}
              {!data && !error && <div className={styles.empty}>Loading…</div>}
            </div>
            {data && (data.kpi_notes.length > 0 || data.pay_hidden) && (
              <div className={styles.notes}>
                {data.pay_hidden && <span className={styles.note}>Labour cost per KG needs payroll access (ADMIN or PAYROLL).</span>}
                {data.kpi_notes.map((n) => (
                  <span key={n} className={styles.note}>
                    {n}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {top > 0 && (
          <div className={top === 2 ? styles.row2 : styles.row1}>
            {boards && (shown('receipters') ? <div className={styles.stack}>{boardCards}</div> : boardCards)}
            {shown('roster') && <Roster roster={roster} failed={rosterError} today={today} onClockAll={() => setClockOpen(true)} />}
          </div>
        )}

        {bottom > 0 && (
          <div className={bottom === 2 ? styles.row2 : styles.row1}>
            {shown('weekly') && <Weekly data={data} />}
            {shown('weather') && <WeatherCard location={location} onLocation={setLocation} />}
          </div>
        )}

        {customOpen && <Customize shown={shown} locked={(id) => optIn(id) && !canSetWorkspace} onToggle={toggleWidget} onClose={() => setCustomOpen(false)} />}
        {clockOpen && <ClockOffDialog user={user} today={today} live={roster?.workers.length || 0} onClose={() => setClockOpen(false)} onDone={clockedOff} />}
        {toast && (
          <div className={pa.toast} role="status">
            {toast.text}
            {toast.undo && (
              <>
                <button type="button" className={styles.toastUndo} onClick={() => undo(toast.undo!)}>
                  Undo
                </button>
                <button type="button" className={styles.toastUndo} aria-label="Dismiss" onClick={() => setToast(null)}>
                  ×
                </button>
              </>
            )}
          </div>
        )}
      </PageBody>
    </>
  )
}

// ─── KPIs ────────────────────────────────────────────────────────────────────

const KPI_ICON: Record<Kpi['key'], string> = {
  kg_items: 'M6 7h12l-1.5 12h-9L6 7ZM9 7a3 3 0 0 1 6 0',
  cost_all: 'M12 3v18M16.5 7.5c-.6-1.2-2.2-2-4.5-2-2.6 0-4 1.2-4 3s1.6 2.6 4 3 4 1.2 4 3-1.4 3-4 3c-2.3 0-3.9-.8-4.5-2',
  cost_pickers: 'M12 3v18M16.5 7.5c-.6-1.2-2.2-2-4.5-2-2.6 0-4 1.2-4 3s1.6 2.6 4 3 4 1.2 4 3-1.4 3-4 3c-2.3 0-3.9-.8-4.5-2',
  kg_hour: 'M12 13l4-4M4 17a8 8 0 1 1 16 0',
}

function KpiCard({ k }: { k: Kpi }) {
  const d = kpiDelta(k)
  return (
    <div className={styles.kpi} data-testid={`kpi-${k.key}`}>
      <div className={styles.kpiHead}>
        <span className={styles.well}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={KPI_ICON[k.key]} />
          </svg>
        </span>
        <span className={styles.kpiLabel}>{k.label}</span>
      </div>
      <div className={styles.kpiValue}>
        {kpiValue(k)}
        {k.key === 'kg_items' && k.items !== undefined && <span className={styles.kpiItems}> / {num(k.items)}</span>}
      </div>
      <div className={`${styles.delta} ${styles[d.tone]}`}>{d.text}</div>
    </div>
  )
}

// ─── leaderboard ─────────────────────────────────────────────────────────────

// The worker leaderboard, and the receipters board above it in the same
// columns: a receipter's Itms, KGs and KG/Hr are what they received for others,
// over their own hours. Receipters have no minimum flag and no low two.
function Leaderboard({
  kind,
  data,
  rows,
  team,
  onTeam,
  onOpen,
  range,
}: {
  kind: 'workers' | 'receipters'
  data: DashResponse | null
  rows: LeaderRow[]
  team: string
  onTeam: (t: string) => void
  onOpen?: (r: LeaderRow) => void
  range: { from: string; to: string }
}) {
  const workers = kind === 'workers'
  // Show all or the top five: saved against the person, like the layout.
  const { user } = useAuth()
  const [all, setAll] = usePreference<boolean>(user, workers ? 'dashboard.leaderboard_all' : 'dashboard.receipters_all', false)
  const { startTask } = useTasks()
  // Top five, a divider standing for everyone between, the bottom two (§7).
  const collapsible = rows.length > 7
  const head = collapsible && !all ? rows.slice(0, 5) : rows
  const tail = collapsible && !all ? rows.slice(-2) : []
  const lowFrom = workers && rows.length > 5 ? rows.length - 2 : Infinity
  const notes = workers ? data?.leaderboard.notes || [] : []
  const title = workers ? 'Worker leaderboard' : 'Receipters'
  const received = 'Items and KGs received for others; KG/Hr over the receipter’s own hours'
  const total = leaderTotal(rows)
  // Without pay_data the server sends no earnings, so there is no Hrly column.
  const pay = !data?.pay_hidden

  const exportRows = () => {
    const teamName = data?.teams.find((t) => t.id === team)?.name || 'All teams'
    const build = () => workbook([
      {
        name: workers ? 'Leaderboard' : 'Receipters',
        rows: [
          ...(pay
            ? [
                ['Rank', 'Name', 'Hours', 'Hourly', 'Items', 'KGs', 'KG/Hr', 'Personal minimum', 'Below minimum'],
                ...rows.map((r) => [r.rank, r.name, r.hours, r.no_picks_yet ? '' : (r.hourly ?? ''), r.items, r.kg, r.kg_hour, r.personal_minimum ?? '', r.below_minimum ? 'Yes' : '']),
                ['', 'Total', total.hours, total.hourly ?? '', total.items, total.kg, total.kg_hour],
              ]
            : [
                ['Rank', 'Name', 'Hours', 'Items', 'KGs', 'KG/Hr'],
                ...rows.map((r) => [r.rank, r.name, r.hours, r.items, r.kg, r.kg_hour]),
                ['', 'Total', total.hours, total.items, total.kg, total.kg_hour],
              ]),
          [],
          [`Period ${range.from} to ${range.to}`],
          [`Team: ${teamName}`],
          ...(workers ? [] : [[received]]),
          ...notes.map((n) => [n]),
        ],
      },
    ])
    const what = workers ? 'leaderboard' : 'receipters'
    const who = workers ? plural(rows.length, 'worker', 'workers') : plural(rows.length, 'receipter', 'receipters')
    startTask(fileTask(`Export ${what}`, `${who} · ${teamName} · ${rangeLabel(range.from, range.to)}`, `${what}-${range.from}-${range.to}.xlsx`, XLSX_TYPE, build))
  }

  const row = (r: LeaderRow) => {
    const topRow = r.rank <= 3
    const low = r.rank - 1 >= lowFrom
    const minTitle =
      r.personal_minimum == null
        ? undefined
        : r.below_minimum
          ? `Below personal minimum of ${money(r.personal_minimum)}/hr: set in Personnel${r.minimum_partial ? ' (recorded for some days only)' : ''}`
          : `Personal minimum ${money(r.personal_minimum)}/hr${r.minimum_partial ? ' (recorded for some days only)' : ''}`
    const cells = (
      <>
        <span className={topRow ? styles.medal : styles.rank} style={topRow ? { background: MEDAL[r.rank - 1] } : undefined}>
          {r.rank}
        </span>
        <span className={styles.name}>{r.name}</span>
        {r.in_progress ? (
          <span className={styles.soFar} title="Still clocked in: hours to the last scan, less the scheduled breaks so far">
            {num(r.hours, 1)}
          </span>
        ) : (
          <span>{num(r.hours, 1)}</span>
        )}
        {pay &&
          (r.no_picks_yet ? (
            <span title="Nothing picked yet this shift">—</span>
          ) : (
            <span title={minTitle}>{r.below_minimum ? <span className={styles.below}>{money(r.hourly ?? 0)}</span> : money(r.hourly ?? 0)}</span>
          ))}
        <span>{num(r.items)}</span>
        <span>{num(r.kg, 1)}</span>
        <span className={low ? styles.kgLow : topRow ? styles.kgTop : undefined}>{num(r.kg_hour, 1)}</span>
      </>
    )
    return onOpen ? (
      <button key={r.human_id} type="button" className={topRow ? styles.lbTop : styles.lbRow} title={`Open ${r.name}'s cycles in Product activity`} onClick={() => onOpen(r)} data-testid="lb-row">
        {cells}
      </button>
    ) : (
      <div key={r.human_id} className={topRow ? styles.lbTop : styles.lbRow} data-testid="rc-row">
        {cells}
      </div>
    )
  }

  return (
    <div className={`${styles.card}${workers ? ` ${styles.tall}` : ''}${pay ? '' : ` ${styles.noPay}`}`} data-testid={workers ? 'widget-leaderboard' : 'widget-receipters'}>
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>{title}</span>
        <label className={styles.pill}>
          Team
          <select aria-label="Team" value={team} onChange={(e) => onTeam(e.target.value)}>
            <option value="">All</option>
            {((workers ? data?.leaderboard.teams : data?.receipters.teams) ?? data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <span className={styles.spacer} />
        <button type="button" className={styles.link} onClick={exportRows} disabled={!rows.length}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
          </svg>
          Download report
        </button>
      </div>
      <div className={styles.lbHead}>
        <span>#</span>
        <span>Name</span>
        <span>Hrs</span>
        {pay && <span>Hrly</span>}
        <span>Itms</span>
        <span>KGs</span>
        <span>KG/Hr</span>
      </div>
      {!data ? (
        <div className={styles.empty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          {workers ? 'No hours recorded' : 'Nobody received for someone else'} for this period{team ? ' in this team' : ''}.
        </div>
      ) : (
        <>
          {head.map(row)}
          {collapsible && !all && (
            <button type="button" className={styles.gapRow} onClick={() => setAll(true)}>
              {rows.length - 7} more {workers ? 'workers' : 'receipters'}: show all ▾
            </button>
          )}
          {tail.map(row)}
          {collapsible && all && (
            <button type="button" className={styles.gapRow} onClick={() => setAll(false)}>
              Show top 5 only ▴
            </button>
          )}
          <div className={styles.lbTotal} data-testid={workers ? 'lb-total' : 'rc-total'}>
            <span className={styles.totalLabel}>
              Total · {workers ? plural(rows.length, 'worker', 'workers') : plural(rows.length, 'receipter', 'receipters')}
            </span>
            <span>{num(total.hours, 1)}</span>
            {pay && (
              <span title="Average, weighted by hours">
                <small className={styles.avg}>avg</small>
                {total.hourly == null ? '—' : money(total.hourly)}
              </span>
            )}
            <span>{num(total.items)}</span>
            <span>{num(total.kg, 1)}</span>
            <span title="Average: total KGs over total hours">
              <small className={styles.avg}>avg</small>
              {num(total.kg_hour, 1)}
            </span>
          </div>
        </>
      )}
      {notes.map((n) => (
        <div key={n} className={styles.note}>
          {n}
        </div>
      ))}
      {!workers ? (
        <div className={styles.note}>{received}.</div>
      ) : pay ? (
        <div className={styles.legendLine}>
          <span className={styles.swatch} />
          Hourly rate below the worker's personal minimum, set in Personnel
        </div>
      ) : (
        <div className={styles.note}>Ranked on KG/Hr. Earnings need payroll access (ADMIN or PAYROLL).</div>
      )}
    </div>
  )
}

// ─── Clocked in now ──────────────────────────────────────────────────────────

function Roster({ roster, failed, today, onClockAll }: { roster: RosterResponse | null; failed: boolean; today: string; onClockAll: () => void }) {
  const all = roster?.workers || []
  const [filter, setFilter] = useState<RosterFilter>(NO_FILTER)
  const opts = rosterOptions(all)
  // A choice nobody clocked in still has falls back to All rather than an empty list.
  const live: RosterFilter = {
    group: filter.group === NONE ? (opts.noGroup ? NONE : '') : opts.groups.includes(filter.group) ? filter.group : '',
    team: opts.teams.includes(filter.team) ? filter.team : '',
    role: filter.role === NONE ? (opts.noRole ? NONE : '') : opts.roles.includes(filter.role) ? filter.role : '',
  }
  const workers = filterRoster(all, live)
  const filtered = live.group || live.team || live.role
  const pick = (label: string, key: keyof RosterFilter, values: string[], none: string | null) => (
    <label className={styles.pill}>
      {label}
      <select aria-label={label} value={live[key]} onChange={(e) => setFilter({ ...live, [key]: e.target.value })}>
        <option value="">All</option>
        {values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
        {none && <option value={NONE}>{none}</option>}
      </select>
    </label>
  )
  return (
    <div className={`${styles.card} ${styles.tall}`} data-testid="widget-roster">
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>Clocked in now</span>
        <span className={styles.live}>
          <span className={styles.liveDot} />
          {filtered ? `${workers.length} of ${all.length}` : workers.length} live
        </span>
      </div>
      {all.length > 0 && (
        <div className={styles.rosterFilters}>
          {pick('Group', 'group', opts.groups, opts.noGroup ? 'No group' : null)}
          {pick('Team', 'team', opts.teams, null)}
          {pick('Role', 'role', opts.roles, opts.noRole ? 'No role' : null)}
        </div>
      )}
      {!roster ? (
        <div className={styles.empty}>{failed ? 'The roster is unavailable right now.' : 'Loading…'}</div>
      ) : all.length === 0 ? (
        <div className={styles.empty}>Nobody is clocked in right now.</div>
      ) : workers.length === 0 ? (
        <div className={styles.empty}>Nobody clocked in matches these filters.</div>
      ) : (
        <div className={styles.rosterList}>
          {workers.map((w, i) => (
            <div key={w.shift_id} className={styles.rosterRow} data-testid="roster-row">
              <span className={styles.avatar} style={{ background: AVATAR[i % 2] }}>
                {initials(w.name)}
              </span>
              <div className={styles.rosterMain}>
                <span className={styles.rosterName}>{w.name}</span>
                <span className={styles.rosterSub}>
                  Clocked on {w.clock_on} · {w.team_name}
                  {w.start_time !== w.clock_on ? ` since ${w.start_time}` : ''}
                  {w.last_scan ? ` · last scan ${w.last_scan.time}${w.last_scan.location ? ` at ${w.last_scan.location}` : ''}` : ''}
                </span>
              </div>
              <span className={styles.count}>
                {num(w.items)}
                <small>items</small>
              </span>
            </div>
          ))}
        </div>
      )}
      {failed && roster && <div className={styles.rosterNote}>Could not refresh; showing the last roster.</div>}
      {roster && roster.earlier_open > 0 && (
        <div className={styles.rosterNote}>
          {roster.earlier_open} earlier shift{roster.earlier_open === 1 ? ' has' : 's have'} no clock-off.{' '}
          <Link to={`/timesheets?date=${addDaysLocal(today, -1)}`} className={styles.link}>
            Fix in Timesheets
          </Link>
        </div>
      )}
      <div className={styles.rosterFoot}>
        <Link to={`/timesheets?date=${today}`} className={styles.link}>
          All {roster?.on_timesheet ?? 0} workers
        </Link>
        <button type="button" className={styles.clockAll} onClick={onClockAll} disabled={!workers.length}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          Clock all off
        </button>
      </div>
    </div>
  )
}

// ─── KGs by week ─────────────────────────────────────────────────────────────

function Weekly({ data }: { data: DashResponse | null }) {
  const w = data?.weekly
  const weeks = w?.weeks || []
  const max = Math.max(0, ...weeks.flatMap((x) => [x.this_season.kg || 0, x.last_season.kg || 0]))
  const first = weeks[0]?.this_season.start
  const last = weeks[weeks.length - 1]?.this_season.start
  const bar = (cls: string, kg: number | null, start: string | null, week: number, label: string) =>
    kg && start && max > 0 ? <span className={cls} style={{ height: `${(kg / max) * 100}%` }} title={`${weekTitle(start, week)} · ${label} ${num(kg, 1)} kg`} data-testid="week-bar" /> : <span className={styles.bar} />
  return (
    <div className={`${styles.card} ${styles.short}`} data-testid="widget-weekly">
      <div className={styles.cardHead}>
        <div>
          <div className={styles.cardTitle}>KGs by week, year on year</div>
          {first && last && (
            <div className={styles.cardSub}>
              {dmy(first)} – {dmy(addDaysLocal(last, 6))}
            </div>
          )}
        </div>
        <span className={styles.spacer} />
        {w && (
          <div className={styles.legendRow}>
            <span>
              <i style={{ background: 'var(--dp-brand)' }} />
              Last season ({w.last_label})
            </span>
            <span>
              <i style={{ background: 'var(--dp-accent)' }} />
              This season ({w.this_label})
            </span>
          </div>
        )}
      </div>
      {!data ? (
        <div className={styles.empty}>Loading…</div>
      ) : max === 0 ? (
        <div className={styles.empty}>No weight recorded in either season yet.</div>
      ) : (
        <div className={styles.chart}>
          {weeks.map((x, i) => (
            <div key={x.this_season.start} className={styles.week}>
              <div className={styles.bars}>
                {bar(styles.barLast, x.last_season.kg, x.last_season.start, x.week, w!.last_label)}
                {bar(styles.barThis, x.this_season.kg, x.this_season.start, x.week, w!.this_label)}
              </div>
              <div className={styles.weekLabel}>{i % 3 === 0 ? `W${x.week}` : ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Customize ───────────────────────────────────────────────────────────────

const WIDGET_ICON: Record<WidgetId, React.ReactNode> = {
  kpis: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  weekly: <path d="M3 21h18M7 16v-5M12 16V7M17 16v-8" />,
  leaderboard: <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4ZM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />,
  roster: <path d="M13 2 4.5 13.5H11l-1 8.5L19 10h-6.5L13 2Z" />,
  receipters: <path d="M3 7h18l-2 12H5L3 7ZM8 7V5a4 4 0 0 1 8 0v2" />,
  weather: <path d="M22 12h-4l-3 8-4-16-3 8H2" />,
}

function Customize({
  shown,
  locked,
  onToggle,
  onClose,
}: {
  shown: (id: WidgetId) => boolean
  locked: (id: WidgetId) => boolean
  onToggle: (id: WidgetId) => void
  onClose: () => void
}) {
  const count = WIDGETS.filter(shown).length
  // A layer (h 425-458): Escape and the scrim close it. Each toggle saves as it
  // is pressed, so there is never anything to discard.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: panelEl, modal: true })
  // V4 "Search widgets…": narrows the list by name or description; it changes nothing that is saved.
  const [q, setQ] = useState('')
  const term = q.trim().toLowerCase()
  const groups = WIDGET_GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => !term || (it.name + ' ' + it.desc).toLowerCase().includes(term)) })).filter((g) => g.items.length)
  return (
    <div className={o.drawerScrim} onClick={requestClose}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${dov.custDrawer}`} role="dialog" aria-label="Customize dashboard" onClick={(e) => e.stopPropagation()}>
        <div className={`${o.drawerHead} ${dov.custHead}`}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>Customize dashboard</h2>
            <p className={dov.custSub}>Toggle widgets on or off. Your choice is saved against you; Receipters is set for everyone in the workspace.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={dov.custSearchRow}>
          <label className={dov.custSearch}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search widgets…" aria-label="Search widgets" />
          </label>
        </div>
        <div className={dov.custBody}>
          {!groups.length && <div className={dov.custNone}>No widget matches that.</div>}
          {groups.map((g) => (
            <div key={g.group}>
              <div className={dov.custGroup}>{g.group}</div>
              <div className={dov.custItems}>
                {g.items.map((it) => (
                  <div key={it.id} className={dov.custItem}>
                    <span className={dov.custIcon} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
                        {WIDGET_ICON[it.id]}
                      </svg>
                    </span>
                    <div className={dov.custText}>
                      <div className={dov.custName}>{it.name}</div>
                      <div className={dov.custDesc}>
                        {it.desc}
                        {locked(it.id) && '; an administrator turns it on or off'}
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={shown(it.id)}
                      aria-label={it.name}
                      className={o.switch}
                      disabled={locked(it.id)}
                      onClick={() => onToggle(it.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className={o.drawerFoot}>
          <span className={dov.custCount}>
            {count} of {WIDGETS.length} shown
          </span>
          <button type="button" className={`${o.primary} ${dov.custDone}`} onClick={requestClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
